import { Card, Col, Flex, Row } from 'antd'
import dayjs from 'dayjs'

import { EpidemiologyCaseForm058E_FinalDiagnosticSection } from 'src/api/requests'
import { Text } from 'src/components'
import {
  dayMonthYearFormatWithDot,
  dayMonthYearHourMinutesFormatWithDot
} from 'src/libs'
import { useLocales } from 'src/locales'

import { useDiseaseOutcomeOptions } from '../useDiseaseOutcomeOptions'

type Props = {
  finalDiagnostic: EpidemiologyCaseForm058E_FinalDiagnosticSection
}

export const EpidemiologyFinalDiagnosticDetailsCard = ({
  finalDiagnostic
}: Props) => {
  const { t, currentLang } = useLocales()
  const { isDeath } = useDiseaseOutcomeOptions()

  // The outcome date is the death date; older forms may still hold a date for
  // another outcome, shown under its former label.
  const showOutcomeDate =
    isDeath(finalDiagnostic.diseaseOutcome?.id) ||
    !!finalDiagnostic.diseaseOutcomeDate

  return (
    <Card title={t('epidemiology.final_diagnostic')}>
      <Row gutter={[24, 24]}>
        <Col span={12}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.final_diagnostic_institution')}</Text>
            <Text weight={400}>{finalDiagnostic.institution?.title}</Text>
          </Flex>
        </Col>

        <Col span={12}>
          <Flex vertical gap={4}>
            <Text>{t('organizational_structure.institution_code')}</Text>
            <Text weight={400}>{finalDiagnostic.institution?.code}</Text>
          </Flex>
        </Col>

        <Col span={12}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.diagnoses_title')}</Text>
            <Flex vertical gap={8}>
              {finalDiagnostic.diagnoses?.map((item, index) => (
                <Text
                  key={index}
                  weight={400}
                >{`${index + 1}. ${item.code} - ${item.name?.[currentLang.value]}`}</Text>
              ))}
            </Flex>
          </Flex>
        </Col>

        <Col span={12}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.final_diagnostic_date')}</Text>
            <Text weight={400}>
              {finalDiagnostic.diagnosisDate
                ? dayjs(finalDiagnostic.diagnosisDate).format(
                    dayMonthYearHourMinutesFormatWithDot
                  )
                : '-'}
            </Text>
          </Flex>
        </Col>

        <Col span={12}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.disease_progress')}</Text>
            <Text weight={400}>
              {finalDiagnostic.diseaseProgress?.name?.[currentLang.value]}
            </Text>
          </Flex>
        </Col>

        <Col span={12}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.disease_outcome')}</Text>
            <Text weight={400}>
              {finalDiagnostic.diseaseOutcome?.name?.[currentLang.value]}
            </Text>
          </Flex>
        </Col>

        {showOutcomeDate && (
          <Col span={12}>
            <Flex vertical gap={4}>
              <Text>
                {t(
                  isDeath(finalDiagnostic.diseaseOutcome?.id)
                    ? 'epidemiology.death_date'
                    : 'epidemiology.disease_outcome_date'
                )}
              </Text>
              <Text weight={400}>
                {finalDiagnostic.diseaseOutcomeDate
                  ? dayjs(finalDiagnostic.diseaseOutcomeDate).format(
                      dayMonthYearFormatWithDot
                    )
                  : '-'}
              </Text>
            </Flex>
          </Col>
        )}
      </Row>
    </Card>
  )
}
